import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Form,
  Modal,
  OverlayTrigger,
  Spinner,
  Table,
  Tooltip,
} from "react-bootstrap";
import {
  deleteSertifikatJuleha,
  getSertifikatJulehaByLembaga,
  insertSertifikatJuleha,
  updateSertifikatJuleha,
} from "../../services/api";
import "./LpkDataPelatihanPenyeliaHalal.css";

const JENIS_PELATIHAN_OPTIONS = [
  {
    kode: "JLH01",
    label: "Pelatihan Juru Sembeli Halal",
  },
  {
    kode: "JLH02",
    label: "Pelatihan SJPH",
  },
  {
    kode: "JLH03",
    label: "Pelatihan Sharia Board",
  },
  {
    kode: "JLH04",
    label: "Pelatihan SDM Syariah",
  },
];

const buildFileUrl = (fileName) => {
  if (!fileName) return "";
  return `https://lembaga.halal.go.id/files/${encodeURIComponent(fileName)}`;
};

const LpkDataPelatihanJphLainnya = () => {
  const [form, setForm] = useState({
    jenisPelatihan: "",
    angkatan: "",
    nik: "",
    nama: "",
    fileSertifikat: null,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submittingAjukan, setSubmittingAjukan] = useState(false);
  const [ajukanProgress, setAjukanProgress] = useState({ done: 0, total: 0 });
  const [alert, setAlert] = useState({ type: "", message: "" });
  const [rows, setRows] = useState([]);
  const [loadingGrid, setLoadingGrid] = useState(false);
  const [gridError, setGridError] = useState("");
  const [editingRow, setEditingRow] = useState(null);
  const [showUpdateConfirm, setShowUpdateConfirm] = useState(false);
  const [pendingUpdate, setPendingUpdate] = useState(null);
  const [submittingDelete, setSubmittingDelete] = useState(false);

  const [filterJenis, setFilterJenis] = useState("");
  const [filterAngkatan, setFilterAngkatan] = useState("");
  const [searchText, setSearchText] = useState("");
  const [angkatanOptions, setAngkatanOptions] = useState([]);
  const [loadingAngkatan, setLoadingAngkatan] = useState(false);
  const [angkatanError, setAngkatanError] = useState("");

  const [appliedJenis, setAppliedJenis] = useState("");
  const [appliedAngkatan, setAppliedAngkatan] = useState("");
  const [gridReloadToken, setGridReloadToken] = useState(0);

  const clearSertifikatInput = () => {
    const fileInput = document.getElementById("filesertifikat");
    if (fileInput) fileInput.value = "";
  };

  useEffect(() => {
    const fetchAngkatanByJenis = async () => {
      if (!filterJenis) {
        setAngkatanOptions([]);
        setFilterAngkatan("");
        setAngkatanError("");
        setRows([]);
        setGridError("");
        return;
      }

      try {
        setLoadingAngkatan(true);
        setAngkatanError("");

        const response = await getSertifikatJulehaByLembaga(filterJenis);
        const data = Array.isArray(response.data?.data) ? response.data.data : [];

        const uniqueAngkatan = Array.from(
          new Set(
            data
              .map((item) => String(item?.Angkatan || "").trim())
              .filter((value) => value !== ""),
          ),
        ).sort((a, b) => Number(a) - Number(b));

        setAngkatanOptions(uniqueAngkatan);
      } catch (error) {
        setAngkatanOptions([]);
        setAngkatanError(
          error.response?.data?.message ||
            "Gagal memuat data angkatan dari server.",
        );
      } finally {
        setLoadingAngkatan(false);
      }
    };

    fetchAngkatanByJenis();
  }, [filterJenis, gridReloadToken]);

  useEffect(() => {
    const fetchGridByJenisAndAngkatan = async () => {
      if (!filterJenis || !filterAngkatan) {
        setRows([]);
        setGridError("");
        return;
      }

      try {
        setLoadingGrid(true);
        setGridError("");
        const response = await getSertifikatJulehaByLembaga(
          filterJenis,
          filterAngkatan,
        );
        const data = Array.isArray(response.data?.data) ? response.data.data : [];
        setRows(data);
      } catch (error) {
        setRows([]);
        setGridError(
          error.response?.data?.message ||
            "Gagal memuat data grid dari server.",
        );
      } finally {
        setLoadingGrid(false);
      }
    };

    fetchGridByJenisAndAngkatan();
  }, [filterJenis, filterAngkatan, gridReloadToken]);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const isPdfFile = (file) => {
    if (!file) return true;
    const name = String(file.name || "");
    const type = String(file.type || "").toLowerCase();
    return /\.pdf$/i.test(name) || type === "application/pdf";
  };

  const handleSertifikatFileChange = (event) => {
    const selectedFile = event.target.files?.[0] || null;

    if (selectedFile && !isPdfFile(selectedFile)) {
      setAlert({
        type: "danger",
        message: "File sertifikat harus berformat PDF (.pdf).",
      });
      event.target.value = "";
      handleChange("fileSertifikat", null);
      return;
    }

    handleChange("fileSertifikat", selectedFile);
  };

  const handleFilterJenisChange = (value) => {
    setFilterJenis(value);
    setFilterAngkatan("");
  };

  const resetForm = ({ keepTraining = false, clearEditing = true } = {}) => {
    setForm((prev) => ({
      jenisPelatihan: keepTraining ? prev.jenisPelatihan : "",
      angkatan: keepTraining ? prev.angkatan : "",
      nik: "",
      nama: "",
      fileSertifikat: null,
    }));
    if (clearEditing) {
      setEditingRow(null);
    }
    clearSertifikatInput();
  };

  const isEditLocked = (row) => {
    const status = String(row?.status || "").trim().toLowerCase();
    return status === "tte sukses" || status === "lunas";
  };

  const getLockedReason = (row) => {
    if (!isEditLocked(row)) return "";
    return "Data dengan status TTE Sukses atau Lunas tidak dapat diubah/hapus.";
  };

  const handleEditRow = (row) => {
    if (!row?.id || isEditLocked(row)) {
      return;
    }

    setEditingRow({
      id: row.id,
      originalStatus: String(row?.status || "").trim(),
      fileSertifikat: String(row?.FileSertifikat || "").trim(),
      fileSertifikatTTE: String(row?.FileSertifikatTTE || "").trim(),
      tanggalTTE: String(row?.Tanggal_TTE || "").trim(),
      userTTE: String(row?.UserTTE || "").trim(),
      noInvoice: String(row?.NoInvoice || "").trim(),
      userPersetujuan: String(row?.user_persetujuan || "").trim(),
      tglPersetujuan: String(row?.tgl_persetujuan || "").trim(),
      tempatLahir: String(row?.Tempat_Lahir || "").trim(),
      tglLahir: String(row?.Tgl_Lahir || "").trim(),
      instansi: String(row?.Instansi || "").trim(),
      sinkron: String(row?.sinkron || "").trim(),
    });

    setForm({
      jenisPelatihan: String(row?.jenis_pelatihan || "").trim(),
      angkatan: String(row?.Angkatan || "").trim(),
      nik: String(row?.NIK || "").trim(),
      nama: String(row?.Nama || "").trim(),
      fileSertifikat: null,
    });
    clearSertifikatInput();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submitInsert = async ({ submittedJenis, submittedAngkatan }) => {
    const payload = new FormData();
    payload.append("NIK", form.nik);
    payload.append("Nama", form.nama);
    payload.append("jenis_pelatihan", form.jenisPelatihan);
    payload.append("Angkatan", form.angkatan);
    payload.append("filesertifikat", form.fileSertifikat);

    setSubmitting(true);
    const response = await insertSertifikatJuleha(payload);

    setAlert({
      type: "success",
      message: response.data?.message || "Data berhasil disimpan.",
    });

    setFilterJenis(submittedJenis);
    setFilterAngkatan(submittedAngkatan);
    setAppliedJenis(submittedJenis);
    setAppliedAngkatan(submittedAngkatan);
    setAngkatanOptions((prev) => {
      const normalized = String(submittedAngkatan || "").trim();
      if (!normalized) return prev;
      if (prev.includes(normalized)) return prev;
      return [...prev, normalized].sort((a, b) => Number(a) - Number(b));
    });
    setGridReloadToken((prev) => prev + 1);
    resetForm({ keepTraining: true, clearEditing: false });
  };

  const submitUpdate = async ({ submittedJenis, submittedAngkatan, updatePayload }) => {
    setSubmitting(true);
    const response = await updateSertifikatJuleha(updatePayload);

    setAlert({
      type: "success",
      message: response.data?.message || "Data berhasil diupdate.",
    });

    setFilterJenis(submittedJenis);
    setFilterAngkatan(submittedAngkatan);
    setAppliedJenis(submittedJenis);
    setAppliedAngkatan(submittedAngkatan);
    setAngkatanOptions((prev) => {
      const normalized = String(submittedAngkatan || "").trim();
      if (!normalized) return prev;
      if (prev.includes(normalized)) return prev;
      return [...prev, normalized].sort((a, b) => Number(a) - Number(b));
    });
    setGridReloadToken((prev) => prev + 1);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAlert({ type: "", message: "" });

    const isEditing = Boolean(editingRow?.id);

    if (!form.jenisPelatihan || !form.angkatan || !form.nik || !form.nama) {
      setAlert({
        type: "danger",
        message: "Jenis pelatihan, angkatan, NIK, dan nama wajib diisi.",
      });
      return;
    }

    if (!isEditing && !form.fileSertifikat) {
      setAlert({
        type: "danger",
        message: "File sertifikat wajib diisi untuk data baru.",
      });
      return;
    }

    if (form.fileSertifikat && !isPdfFile(form.fileSertifikat)) {
      setAlert({
        type: "danger",
        message: "File sertifikat harus berformat PDF (.pdf).",
      });
      return;
    }

    const submittedJenis = form.jenisPelatihan;
    const submittedAngkatan = form.angkatan;

    if (isEditing) {
      const updatePayload = new FormData();
      updatePayload.append("id", editingRow.id);
      updatePayload.append("NIK", form.nik);
      updatePayload.append("Nama", form.nama);
      updatePayload.append("Angkatan", form.angkatan);
      updatePayload.append("jenis_pelatihan", form.jenisPelatihan);
      updatePayload.append("status", editingRow.originalStatus || "");
      updatePayload.append("FileSertifikat", editingRow.fileSertifikat || "");
      updatePayload.append("FileSertifikatTTE", editingRow.fileSertifikatTTE || "");
      updatePayload.append("Tanggal_TTE", editingRow.tanggalTTE || "");
      updatePayload.append("UserTTE", editingRow.userTTE || "");
      updatePayload.append("NoInvoice", editingRow.noInvoice || "");
      updatePayload.append("user_persetujuan", editingRow.userPersetujuan || "");
      updatePayload.append("tgl_persetujuan", editingRow.tglPersetujuan || "");
      updatePayload.append("Tempat_Lahir", editingRow.tempatLahir || "");
      updatePayload.append("Tgl_Lahir", editingRow.tglLahir || "");
      updatePayload.append("Instansi", editingRow.instansi || "");
      updatePayload.append("sinkron", editingRow.sinkron || "");
      if (form.fileSertifikat) {
        updatePayload.append("filesertifikat", form.fileSertifikat);
      }

      setPendingUpdate({
        submittedJenis,
        submittedAngkatan,
        updatePayload,
      });
      setShowUpdateConfirm(true);
      return;
    }

    try {
      await submitInsert({ submittedJenis, submittedAngkatan });
    } catch (error) {
      setAlert({
        type: "danger",
        message:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Gagal menyimpan data JPH lainnya.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmUpdate = async () => {
    if (!pendingUpdate) return;

    try {
      await submitUpdate(pendingUpdate);
      setShowUpdateConfirm(false);
      setPendingUpdate(null);
    } catch (error) {
      setAlert({
        type: "danger",
        message:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Gagal memperbarui data JPH lainnya.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteRow = async (row) => {
    if (!row?.id || isEditLocked(row) || submittingDelete) return;

    const confirmed = window.confirm(
      `Hapus data ${row?.Nama || ""} (${row?.NIK || "-"})?`,
    );
    if (!confirmed) return;

    try {
      setSubmittingDelete(true);
      const response = await deleteSertifikatJuleha({ id: row.id });
      setAlert({
        type: "success",
        message: response.data?.message || "Data berhasil dihapus.",
      });
      setGridReloadToken((prev) => prev + 1);

      if (editingRow?.id === row.id) {
        resetForm();
      }
    } catch (error) {
      setAlert({
        type: "danger",
        message:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Gagal menghapus data JPH lainnya.",
      });
    } finally {
      setSubmittingDelete(false);
    }
  };

  const handleAjukanStatus = async () => {
    if (submittingAjukan) return;

    const rowsToUpdate = filteredRows.filter((row) => {
      const currentStatus = String(row?.status || "").trim();
      return !currentStatus;
    });

    const validRows = rowsToUpdate.filter((row) => row?.id && row?.Nama);

    if (rowsToUpdate.length === 0) {
      setAlert({
        type: "info",
        message:
          "Tidak ada data berstatus kosong pada grid yang bisa diajukan.",
      });
      return;
    }

    if (validRows.length === 0) {
      setAlert({
        type: "warning",
        message:
          "Data berstatus kosong ditemukan, tetapi id atau nama tidak lengkap untuk proses ajukan.",
      });
      return;
    }

    try {
      setSubmittingAjukan(true);
      setAjukanProgress({ done: 0, total: validRows.length });

      const results = await Promise.all(
        validRows.map((row) =>
          updateSertifikatJuleha({
            id: row.id,
            Nama: row.Nama,
            jenis_pelatihan: row.jenis_pelatihan || row.Jenis_Pelatihan || "",
            status: "Pengajuan",
          })
            .then(() => ({ ok: true }))
            .catch((error) => ({ ok: false, error }))
            .finally(() => {
              setAjukanProgress((prev) => ({
                ...prev,
                done: Math.min(prev.done + 1, prev.total),
              }));
            }),
        ),
      );

      const failedCount = results.filter((item) => !item.ok).length;
      const successCount = results.length - failedCount;

      if (failedCount === 0) {
        setAlert({
          type: "success",
          message: `${successCount} data berhasil diajukan.`,
        });
      } else {
        setAlert({
          type: "warning",
          message: `${successCount} data berhasil diajukan, ${failedCount} data gagal diproses.`,
        });
      }

      if (successCount > 0) {
        setGridReloadToken((prev) => prev + 1);
      }
    } catch (error) {
      setAlert({
        type: "danger",
        message:
          error.response?.data?.message ||
          "Gagal mengubah status menjadi Pengajuan.",
      });
    } finally {
      setSubmittingAjukan(false);
      setAjukanProgress((prev) => ({ ...prev, total: 0 }));
    }
  };

  const handleCariFilter = () => {
    setAppliedJenis(filterJenis);
    setAppliedAngkatan(filterAngkatan);
  };

  const handleResetFilter = () => {
    setSearchText("");
    setFilterJenis("");
    setFilterAngkatan("");
    setAngkatanOptions([]);
    setAngkatanError("");
    setRows([]);
    setGridError("");
    setAppliedJenis("");
    setAppliedAngkatan("");
  };

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const rowJenis = String(row.jenis_pelatihan || "").toUpperCase();
      const rowAngkatan = String(row.Angkatan || "");

      const matchJenis = !appliedJenis || rowJenis === String(appliedJenis).toUpperCase();
      const matchAngkatan = !appliedAngkatan || rowAngkatan === String(appliedAngkatan);

      const q = searchText.trim().toLowerCase();
      const matchSearch =
        !q ||
        String(row.NIK || "").toLowerCase().includes(q) ||
        String(row.Nama || "").toLowerCase().includes(q);

      return matchJenis && matchAngkatan && matchSearch;
    });
  }, [rows, appliedJenis, appliedAngkatan, searchText]);

  const isAjukanLocked = useMemo(() => {
    return filteredRows.some((row) => {
      const status = String(row?.status || "").trim().toLowerCase();
      return status === "tte sukses" || status === "lunas";
    });
  }, [filteredRows]);

  useEffect(() => {
    setAppliedJenis(filterJenis);
    setAppliedAngkatan(filterAngkatan);
  }, [filterJenis, filterAngkatan]);

  return (
    <div className="lpk-penyelia-page">
      <Card className="lpk-main-card">
        <Card.Header className="lpk-card-header">Data Pelatihan JPH Lainnya</Card.Header>
        <Card.Body>
          {alert.message && (
            <Alert
              variant={alert.type}
              className="mb-3"
              onClose={() => setAlert({ type: "", message: "" })}
              dismissible
            >
              {alert.message}
            </Alert>
          )}

          <Form onSubmit={handleSubmit} className="lpk-form-block">
            {editingRow && (
              <Alert variant="warning" className="mb-3">
                Mode edit aktif. Simpan untuk memperbarui data yang dipilih.
              </Alert>
            )}

            <Form.Group className="lpk-form-row" controlId="jenisPelatihan">
              <Form.Label>Jenis Pelatihan</Form.Label>
              <Form.Select
                value={form.jenisPelatihan}
                onChange={(e) => handleChange("jenisPelatihan", e.target.value)}
              >
                <option value="">Pilih Jenis Pelatihan</option>
                {JENIS_PELATIHAN_OPTIONS.map((item) => (
                  <option key={item.kode} value={item.kode}>
                    {item.kode} - {item.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="lpk-form-row lpk-angkatan-row" controlId="angkatan">
              <Form.Label>Angkatan</Form.Label>
              <Form.Control
                type="text"
                value={form.angkatan}
                onChange={(e) => handleChange("angkatan", e.target.value)}
                placeholder="Masukkan angkatan"
              />
            </Form.Group>

            <Form.Group className="lpk-form-row" controlId="nik">
              <Form.Label>NIK</Form.Label>
              <Form.Control
                type="text"
                value={form.nik}
                onChange={(e) => handleChange("nik", e.target.value)}
                placeholder="Masukkan NIK"
              />
            </Form.Group>

            <Form.Group className="lpk-form-row" controlId="nama">
              <Form.Label>Nama</Form.Label>
              <Form.Control
                type="text"
                value={form.nama}
                onChange={(e) => handleChange("nama", e.target.value)}
                placeholder="Masukkan nama"
              />
            </Form.Group>

            <Form.Group className="lpk-form-row" controlId="filesertifikat">
              <Form.Label>File Sertifikat</Form.Label>
              <Form.Control
                type="file"
                accept=".pdf,application/pdf"
                onChange={handleSertifikatFileChange}
              />
              <Form.Text className="text-muted">
                Format file yang diterima: PDF (.pdf), ukuran maksimum 10MB.
              </Form.Text>
              {editingRow && (
                <Form.Text className="text-muted">
                  Opsional: upload file baru jika ingin mengganti sertifikat lama.
                </Form.Text>
              )}
            </Form.Group>

            <div className="lpk-button-row">
              <Button variant="primary" type="submit" disabled={submitting}>
                {submitting ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Menyimpan...
                  </>
                ) : (
                  editingRow ? "Update" : "Simpan"
                )}
              </Button>
              <Button variant="secondary" type="button" onClick={resetForm} disabled={submitting}>
                {editingRow ? "Batal Edit" : "Batal"}
              </Button>
            </div>
          </Form>

          <hr className="my-4" />

          <div className="lpk-filter-block">
            <Form.Group className="lpk-form-row" controlId="filterJenis">
              <Form.Label>Jenis Pelatihan</Form.Label>
              <Form.Select
                value={filterJenis}
                onChange={(e) => handleFilterJenisChange(e.target.value)}
              >
                <option value="">Pilih Jenis Pelatihan</option>
                {JENIS_PELATIHAN_OPTIONS.map((item) => (
                  <option key={item.kode} value={item.kode}>
                    {item.kode} - {item.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <div className="lpk-filter-submit-row">
              <Form.Group className="lpk-form-row" controlId="filterAngkatan">
                <Form.Label>Angkatan</Form.Label>
                <Form.Select
                  value={filterAngkatan}
                  onChange={(e) => setFilterAngkatan(e.target.value)}
                  disabled={!filterJenis || loadingAngkatan}
                >
                  <option value="">Semua Angkatan</option>
                  {angkatanOptions.map((angkatan) => (
                    <option key={angkatan} value={angkatan}>
                      {angkatan}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Button
                variant="primary"
                type="button"
                onClick={handleAjukanStatus}
                disabled={submittingAjukan || loadingGrid || isAjukanLocked}
              >
                {submittingAjukan ? "Mengajukan..." : "Ajukan"}
              </Button>
            </div>

            {loadingAngkatan && (
              <div className="lpk-inline-info">Memuat daftar angkatan...</div>
            )}
            {angkatanError && (
              <div className="lpk-inline-error">{angkatanError}</div>
            )}
            {submittingAjukan && ajukanProgress.total > 0 && (
              <div className="lpk-inline-info">
                Mengajukan data... {ajukanProgress.done}/{ajukanProgress.total}
              </div>
            )}

            <div className="lpk-search-row">
              <Form.Group className="lpk-form-row lpk-search-input" controlId="search">
                <Form.Label>Cari NIK / Nama</Form.Label>
                <Form.Control
                  type="text"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder="Masukkan NIK atau Nama"
                />
              </Form.Group>
              <Button variant="info" type="button" onClick={handleCariFilter}>
                Cari
              </Button>
              <Button variant="secondary" type="button" onClick={handleResetFilter}>
                Reset
              </Button>
            </div>
          </div>

          <div className="table-responsive mt-3">
            {gridError && <div className="lpk-inline-error mb-2">{gridError}</div>}
            <Table bordered hover size="sm" className="mb-0 lpk-table">
              <thead>
                <tr>
                  <th>No</th>
                  <th>NIK</th>
                  <th>Jenis Pelatihan</th>
                  <th>Nama</th>
                  <th>Angkatan</th>
                  <th>Status</th>
                  <th>Sertifikat</th>
                  <th>Sertifikat (Sudah TTE)</th>
                  <th>Edit</th>
                  <th>Hapus</th>
                </tr>
              </thead>
              <tbody>
                {loadingGrid ? (
                  <tr>
                    <td colSpan={10}>Memuat data...</td>
                  </tr>
                ) : filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={10}>Tidak ada data ditemukan</td>
                  </tr>
                ) : (
                  filteredRows.map((row, idx) => (
                    <tr key={row.id || `${row.NIK}-${idx}`}>
                      <td>{idx + 1}</td>
                      <td>{row.NIK || "-"}</td>
                      <td>{row.jenis_pelatihan || "-"}</td>
                      <td>{row.Nama || "-"}</td>
                      <td>{row.Angkatan || "-"}</td>
                      <td>{row.status || "-"}</td>
                      <td>
                        {row.FileSertifikat ? (
                          <a
                            href={buildFileUrl(row.FileSertifikat)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {row.FileSertifikat}
                          </a>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td>
                        {row.FileSertifikatTTE ? (
                          <a
                            href={buildFileUrl(row.FileSertifikatTTE)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {row.FileSertifikatTTE}
                          </a>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td>
                        {isEditLocked(row) ? (
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip id={`edit-locked-${row.id}`}>{getLockedReason(row)}</Tooltip>}
                          >
                            <span className="d-inline-block">
                              <Button size="sm" variant="outline-primary" type="button" disabled>
                                Edit
                              </Button>
                            </span>
                          </OverlayTrigger>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline-primary"
                            type="button"
                            onClick={() => handleEditRow(row)}
                          >
                            Edit
                          </Button>
                        )}
                      </td>
                      <td>
                        {isEditLocked(row) ? (
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip id={`delete-locked-${row.id}`}>{getLockedReason(row)}</Tooltip>}
                          >
                            <span className="d-inline-block">
                              <Button size="sm" variant="outline-danger" type="button" disabled>
                                Hapus
                              </Button>
                            </span>
                          </OverlayTrigger>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline-danger"
                            type="button"
                            onClick={() => handleDeleteRow(row)}
                            disabled={submittingDelete}
                          >
                            Hapus
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>

          <Modal
            show={showUpdateConfirm}
            onHide={() => {
              if (!submitting) {
                setShowUpdateConfirm(false);
                setPendingUpdate(null);
              }
            }}
            centered
          >
            <Modal.Header closeButton={!submitting}>
              <Modal.Title>Konfirmasi Update</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              Data akan diperbarui. Lanjutkan proses update?
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => {
                  if (!submitting) {
                    setShowUpdateConfirm(false);
                    setPendingUpdate(null);
                  }
                }}
                disabled={submitting}
              >
                Batal
              </Button>
              <Button variant="primary" onClick={handleConfirmUpdate} disabled={submitting}>
                {submitting ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Mengupdate...
                  </>
                ) : (
                  "Ya, Update"
                )}
              </Button>
            </Modal.Footer>
          </Modal>
        </Card.Body>
      </Card>
    </div>
  );
};

export default LpkDataPelatihanJphLainnya;
